import { memo } from 'react';

/**
 * Memoised tree SVG — one variant per environment.
 * Props: tw (width), th (height), variant (environment name)
 * Memo ensures the SVG DOM is built once per (tw, th, variant) combo, not every frame.
 */
export const TreeSvg = memo(function TreeSvg({ tw, th, variant = 'forest' }) {
  let inner;
  if (variant === 'desert') {
    inner = <>
      <ellipse cx="30" cy="82" rx="12" ry="3" fill="rgba(0,0,0,0.20)"/>
      {/* Arms */}
      <rect x="40" y="38" width="9" height="14" rx="4.5" fill="#4a9a28" stroke="#2d6a14" strokeWidth="1"/>
      <rect x="36" y="30" width="7" height="12" rx="3.5" fill="#5aaa30" stroke="#2d6a14" strokeWidth="1"/>
      <rect x="11" y="44" width="9" height="14" rx="4.5" fill="#4a9a28" stroke="#2d6a14" strokeWidth="1"/>
      <rect x="14" y="36" width="7" height="12" rx="3.5" fill="#5aaa30" stroke="#2d6a14" strokeWidth="1"/>
      {/* Body */}
      <rect x="23" y="8" width="14" height="74" rx="7" fill="#5aaa2a" stroke="#3a7a14" strokeWidth="1.2"/>
      {/* Spines */}
      <line x1="23" y1="22" x2="17" y2="19" stroke="#d4c860" strokeWidth="0.9" opacity="0.9"/>
      <line x1="37" y1="22" x2="43" y2="19" stroke="#d4c860" strokeWidth="0.9" opacity="0.9"/>
      <line x1="23" y1="38" x2="17" y2="35" stroke="#d4c860" strokeWidth="0.9" opacity="0.9"/>
      <line x1="37" y1="38" x2="43" y2="35" stroke="#d4c860" strokeWidth="0.9" opacity="0.9"/>
      <line x1="23" y1="54" x2="17" y2="51" stroke="#d4c860" strokeWidth="0.9" opacity="0.9"/>
      <line x1="37" y1="54" x2="43" y2="51" stroke="#d4c860" strokeWidth="0.9" opacity="0.9"/>
      {/* Top flower */}
      <ellipse cx="30" cy="10" rx="6" ry="4" fill="#88cc30" stroke="#4a8a18" strokeWidth="0.8"/>
    </>;
  } else if (variant === 'snow') {
    inner = <>
      <ellipse cx="30" cy="82" rx="12" ry="3" fill="rgba(0,0,0,0.18)"/>
      {/* Trunk */}
      <rect x="25" y="60" width="10" height="22" rx="3" fill="#6a3a18" stroke="#4a2a10" strokeWidth="1"/>
      {/* Bottom layer */}
      <polygon points="30,36 8,72 52,72" fill="#1e7020" stroke="#145c14" strokeWidth="1"/>
      <path d="M 8 72 Q 20 62 30 60 Q 40 62 52 72" fill="rgba(230,240,255,0.88)"/>
      {/* Mid layer */}
      <polygon points="30,20 12,54 48,54" fill="#2a8a28" stroke="#1e6a1e" strokeWidth="1"/>
      <path d="M 12 54 Q 22 46 30 44 Q 38 46 48 54" fill="rgba(230,240,255,0.88)"/>
      {/* Top layer */}
      <polygon points="30,6 18,36 42,36" fill="#34a030" stroke="#268020" strokeWidth="1"/>
      <path d="M 18 36 Q 24 30 30 28 Q 36 30 42 36" fill="rgba(230,240,255,0.92)"/>
      {/* Star tip */}
      <circle cx="30" cy="7" r="2.5" fill="white" opacity="0.9"/>
    </>;
  } else if (variant === 'castle') {
    inner = <>
      <ellipse cx="30" cy="82" rx="10" ry="3" fill="rgba(0,0,0,0.28)"/>
      {/* Trunk */}
      <rect x="26" y="44" width="8" height="38" rx="3" fill="#3a3028" stroke="#26201c" strokeWidth="1"/>
      {/* Main branches */}
      <line x1="30" y1="44" x2="10" y2="22" stroke="#3a3028" strokeWidth="5" strokeLinecap="round"/>
      <line x1="30" y1="44" x2="50" y2="24" stroke="#3a3028" strokeWidth="5" strokeLinecap="round"/>
      <line x1="30" y1="36" x2="12" y2="16" stroke="#3a3028" strokeWidth="4" strokeLinecap="round"/>
      <line x1="30" y1="38" x2="46" y2="14" stroke="#3a3028" strokeWidth="4" strokeLinecap="round"/>
      {/* Small branches */}
      <line x1="10" y1="22" x2="4"  y2="14" stroke="#3a3028" strokeWidth="2.5" strokeLinecap="round"/>
      <line x1="10" y1="22" x2="14" y2="10" stroke="#3a3028" strokeWidth="2.5" strokeLinecap="round"/>
      <line x1="50" y1="24" x2="56" y2="16" stroke="#3a3028" strokeWidth="2.5" strokeLinecap="round"/>
      <line x1="50" y1="24" x2="46" y2="10" stroke="#3a3028" strokeWidth="2.5" strokeLinecap="round"/>
      {/* Moss patches */}
      <ellipse cx="18" cy="20" rx="4" ry="2.5" fill="#3a5030" opacity="0.55"/>
      <ellipse cx="44" cy="22" rx="4" ry="2.5" fill="#3a5030" opacity="0.55"/>
    </>;
  } else if (variant === 'water') {
    inner = <>
      <ellipse cx="30" cy="82" rx="14" ry="3" fill="rgba(0,0,0,0.22)"/>
      {/* Trunk */}
      <rect x="26" y="38" width="8" height="44" rx="4" fill="#4a5a28" stroke="#343e1c" strokeWidth="1"/>
      {/* Canopy */}
      <ellipse cx="30" cy="30" rx="24" ry="18" fill="#1a7054" stroke="#0e4838" strokeWidth="1"/>
      <ellipse cx="30" cy="22" rx="18" ry="13" fill="#228862" stroke="#165844" strokeWidth="1"/>
      {/* Drooping tendrils */}
      <path d="M14,32 Q10,52 13,62" stroke="#1a7054" strokeWidth="2" fill="none" strokeLinecap="round"/>
      <path d="M21,40 Q17,58 19,68" stroke="#1a7054" strokeWidth="2" fill="none" strokeLinecap="round"/>
      <path d="M39,40 Q43,56 41,66" stroke="#1a7054" strokeWidth="2" fill="none" strokeLinecap="round"/>
      <path d="M46,32 Q50,50 47,60" stroke="#1a7054" strokeWidth="2" fill="none" strokeLinecap="round"/>
      {/* Lily pad accents */}
      <ellipse cx="17" cy="30" rx="4" ry="2.5" fill="#2aaa70" opacity="0.65"/>
      <ellipse cx="43" cy="27" rx="4" ry="2.5" fill="#2aaa70" opacity="0.65"/>
    </>;
  } else if (variant === 'graveyard') {
    inner = <>
      <ellipse cx="30" cy="82" rx="10" ry="3" fill="rgba(0,0,0,0.35)"/>
      {/* Trunk */}
      <rect x="26" y="42" width="8" height="40" rx="3" fill="#28282e" stroke="#18181e" strokeWidth="1"/>
      {/* Gnarled branches */}
      <line x1="30" y1="42" x2="6"  y2="16" stroke="#28282e" strokeWidth="6" strokeLinecap="round"/>
      <line x1="30" y1="42" x2="54" y2="18" stroke="#28282e" strokeWidth="5" strokeLinecap="round"/>
      <line x1="30" y1="34" x2="8"  y2="6"  stroke="#28282e" strokeWidth="4" strokeLinecap="round"/>
      <line x1="30" y1="32" x2="52" y2="4"  stroke="#28282e" strokeWidth="3" strokeLinecap="round"/>
      {/* Claw tips */}
      <line x1="6"  y1="16" x2="2"  y2="8"  stroke="#28282e" strokeWidth="2" strokeLinecap="round"/>
      <line x1="6"  y1="16" x2="10" y2="6"  stroke="#28282e" strokeWidth="2" strokeLinecap="round"/>
      <line x1="54" y1="18" x2="58" y2="10" stroke="#28282e" strokeWidth="2" strokeLinecap="round"/>
      {/* Eerie purple glow at base */}
      <ellipse cx="30" cy="44" rx="7" ry="3.5" fill="#5544aa" opacity="0.35"/>
      {/* Hanging orb */}
      <line x1="20" y1="24" x2="20" y2="36" stroke="#38384a" strokeWidth="1.2" opacity="0.8"/>
      <ellipse cx="20" cy="38" rx="3" ry="2.5" fill="#4a4470" opacity="0.9"/>
    </>;
  } else if (variant === 'inferno') {
    inner = <>
      <ellipse cx="30" cy="82" rx="12" ry="3" fill="rgba(0,0,0,0.35)"/>
      {/* Charred trunk */}
      <rect x="24" y="38" width="12" height="44" rx="5" fill="#160c08" stroke="#0e0604" strokeWidth="1"/>
      {/* Charred branches */}
      <line x1="30" y1="38" x2="12" y2="16" stroke="#160c08" strokeWidth="5" strokeLinecap="round"/>
      <line x1="30" y1="38" x2="48" y2="14" stroke="#160c08" strokeWidth="5" strokeLinecap="round"/>
      <line x1="12" y1="16" x2="6"  y2="8"  stroke="#160c08" strokeWidth="3" strokeLinecap="round"/>
      <line x1="48" y1="14" x2="54" y2="6"  stroke="#160c08" strokeWidth="3" strokeLinecap="round"/>
      {/* Flames outer→inner */}
      <ellipse cx="30" cy="26" rx="22" ry="18" fill="#e84000" opacity="0.55"/>
      <ellipse cx="30" cy="20" rx="17" ry="14" fill="#f86000" opacity="0.62"/>
      <ellipse cx="30" cy="15" rx="13" ry="11" fill="#ffaa00" opacity="0.70"/>
      <ellipse cx="30" cy="10" rx="9"  ry="8"  fill="#ffcc00" opacity="0.78"/>
      <ellipse cx="30" cy="7"  rx="5"  ry="6"  fill="#fff060" opacity="0.90"/>
      {/* Embers */}
      <circle cx="20" cy="22" r="1.5" fill="#ff8800" opacity="0.85"/>
      <circle cx="40" cy="18" r="1.5" fill="#ff8800" opacity="0.85"/>
      <circle cx="15" cy="30" r="1"   fill="#ffcc00" opacity="0.75"/>
      <circle cx="45" cy="28" r="1"   fill="#ffcc00" opacity="0.75"/>
    </>;
  } else {
    // forest (default)
    inner = <>
      <ellipse cx="30" cy="80" rx="16" ry="4" fill="rgba(0,0,0,0.22)"/>
      <rect x="23" y="54" width="14" height="26" rx="4" fill="#7a4a28" stroke="#5a3018" strokeWidth="1.2"/>
      <line x1="27" y1="58" x2="26" y2="76" stroke="#5a3018" strokeWidth="0.8" strokeLinecap="round" opacity="0.6"/>
      <line x1="33" y1="60" x2="34" y2="74" stroke="#5a3018" strokeWidth="0.8" strokeLinecap="round" opacity="0.6"/>
      <ellipse cx="30" cy="48" rx="26" ry="20" fill="#145c14" stroke="#0d3d0d" strokeWidth="1"/>
      <ellipse cx="30" cy="37" rx="22" ry="18" fill="#1e7a1e" stroke="#145c14" strokeWidth="1"/>
      <ellipse cx="30" cy="26" rx="18" ry="15" fill="#2a9a2a" stroke="#1e7a1e" strokeWidth="1"/>
      <ellipse cx="30" cy="16" rx="12" ry="10" fill="#34b434" stroke="#2a9a2a" strokeWidth="1"/>
      <ellipse cx="24" cy="12" rx="5" ry="3.5" fill="#5ad45a" opacity="0.55"/>
      <circle cx="20" cy="38" r="2" fill="#cc3333" opacity="0.75"/>
      <circle cx="38" cy="32" r="2" fill="#cc3333" opacity="0.75"/>
      <circle cx="27" cy="44" r="1.5" fill="#cc3333" opacity="0.65"/>
    </>;
  }
  return (
    <svg width={tw} height={th} viewBox="0 0 60 84" fill="none"
      xmlns="http://www.w3.org/2000/svg" style={{ display: 'block' }}
    >
      {inner}
    </svg>
  );
});
